import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useEffect, useState } from "react";
import { Icon, LoadingBone, Tag } from "src/components/atoms";
import { IconTooltip } from "src/components/molecules/IconTooltip";
import {
  CutoffDaysRemaining,
  OptionListContainer,
  SkeletonOptionListItem,
} from "src/pages/configurator/components/options";
import { Css, Palette } from "~generated/css";
import { useTestIds } from "~utils";
import { cardVariants, CommonOptionCardProps, TitleActionText } from "./cards";

type AccordionOptionCardProps = {
  children: ReactNode;
  counter?: {
    current: number;
    total: number;
  };
  /** Defaults to true */
  expanded?: boolean;
  onExpanded?: () => void;
} & CommonOptionCardProps;

export function AccordionOptionCard(props: AccordionOptionCardProps) {
  const {
    title,
    description,
    children,
    titleAction,
    infoTooltip,
    counter,
    contrast = false,
    expanded = true,
    onExpanded,
    cutoffDate,
  } = props;
  const [open, setOpen] = useState(expanded);
  const tid = useTestIds(props, "optionCard");
  useEffect(() => {
    setOpen(expanded);
  }, [expanded]);

  return (
    <motion.div
      id="accordion-option-card"
      initial={expanded ? "open" : "closed"}
      variants={cardVariants}
      animate={open ? "open" : "closed"}
      css={Css.px2.pt2.w100.gap1.br4.df.fdc.if(!open).pb2.$}
      {...tid}
    >
      <div
        css={Css.cursorPointer.df.fdc.gap1.if(open).pb1.$}
        onClick={() => {
          setOpen((prev) => !prev);
          onExpanded?.();
        }}
      >
        {/* Header Area */}
        {title && (
          <div css={Css.df.jcsb.relative.top0.$}>
            <div css={Css.df.jcsb.aic.fg1.$}>
              <div
                css={{
                  ...Css.gap1.t18.gray800.$,
                  ...Css.if(contrast).white.$,
                }}
                {...tid.title}
              >
                {title}
              </div>
              {infoTooltip && <IconTooltip message={infoTooltip} type="info" {...tid.infoTooltip} />}
            </div>
            {/* Using isolation here to prevent the tag from being higher than tooltips*/}
            <div id="card-icon-container" css={Css.df.aic.gap1.add({ isolation: "isolate" }).$}>
              {counter && (
                <Tag size="med" backgroundColor={Palette.Blue50}>{`${counter.current} of ${counter.total}`}</Tag>
              )}
              {titleAction && (
                // Note: this padding right is because our chevron icon is kinda big but we're supposed to have an 8px gap
                <div css={Css.df.aic.$}>
                  <TitleActionText
                    contrast={contrast}
                    triggerText={titleAction.triggerText}
                    onClick={titleAction.onClick}
                    size={titleAction.size}
                    {...tid.titleActionText}
                  />
                </div>
              )}
              <CutoffDaysRemaining cutoffDate={cutoffDate} />
              <motion.div
                initial={false}
                css={
                  Css.addIn("svg path", {
                    ...Css.fGray700.$,
                    ...Css.if(contrast).fWhite.$,
                  }).$
                }
                variants={{
                  closed: {
                    rotate: 0,
                  },
                  open: {
                    rotate: 180,
                  },
                }}
              >
                <Icon name="arrow-up" />
              </motion.div>
            </div>
          </div>
        )}
        {/* Main body */}
        {open && description}
      </div>
      {/* Expandable Children */}
      <AnimatePresence>
        {open && (
          <motion.div
            layout="position"
            initial={expanded ? "open" : "closed"}
            animate="open"
            exit="closed"
            css={Css.relative.oh.$}
            variants={{
              closed: {
                height: 0,
                transition: {
                  duration: 0.4,
                  bounce: 1,
                },
                transitionEnd: {
                  zIndex: -1,
                },
              },
              // FIXME: This transition is instant
              // -  We can fix it by passing variants ({ "open", "closed" }) down to our option list and list items
              // -  Bonus points if applying the same to `StepWrapper` will fix the scroll bar resizing all crazy
              open: {
                height: "auto",
                transition: {
                  duration: 0.4,
                },
                transitionEnd: {
                  zIndex: 0,
                },
              },
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function SkeletonAccordionOptionCard() {
  return (
    <div css={Css.p2.w100.gap1.br4.df.fdc.$}>
      <div css={Css.df.fdc.gap1.pb1.$}>
        {/* Header Area */}
        <div css={Css.df.jcsb.relative.top0.$}>
          <div css={Css.df.jcsb.aic.fg1.$}>
            <div css={Css.gap1.t18.gray800.$}>
              <LoadingBone width="115px" height="20px" />
            </div>
          </div>
          {/* Using isolation here to prevent the tag from being higher than tooltips*/}
          <div css={Css.df.aic.gap1.$}>
            <div css={Css.addIn("svg path", Css.fGray700.$).$}>
              <Icon name="arrow-up" />
            </div>
          </div>
        </div>
      </div>
      {/* Expandable Children */}
      <div css={Css.oh.ha.$}>
        <OptionListContainer>
          <SkeletonOptionListItem />
          <SkeletonOptionListItem />
          <SkeletonOptionListItem />
        </OptionListContainer>
      </div>
    </div>
  );
}
